import { useState, useEffect, useRef } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { X, Search } from 'lucide-react';
import { addHolding } from '../api/portfolio';
import { searchStocks, StockSearchResult } from '../api/stocks';

interface AddHoldingModalProps {
  onClose: () => void;
}

export default function AddHoldingModal({ onClose }: AddHoldingModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<StockSearchResult[]>([]);
  const [selectedSymbol, setSelectedSymbol] = useState('');
  const [shares, setShares] = useState('');
  const [totalAmount, setTotalAmount] = useState('');
  const [purchasePrice, setPurchasePrice] = useState('');
  const [purchaseDate, setPurchaseDate] = useState('');
  const [notes, setNotes] = useState('');
  const [currentPrice, setCurrentPrice] = useState<number | null>(null);
  const [inputMode, setInputMode] = useState<'shares' | 'amount'>('shares');
  const [isSearching, setIsSearching] = useState(false);
  const [manualEntry, setManualEntry] = useState(false);
  const [manualCurrency, setManualCurrency] = useState('GBP');
  const queryClient = useQueryClient();
  const searchTimeoutRef = useRef<number | null>(null);

  const addMutation = useMutation({
    mutationFn: ({ symbol, shares, purchasePrice, purchaseDate, notes }: { 
      symbol: string; 
      shares: number;
      purchasePrice?: number;
      purchaseDate?: number;
      notes?: string;
    }) =>
      addHolding(symbol, shares, purchasePrice, purchaseDate, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['portfolio'] });
      onClose();
    },
  });

  const handleSearch = async (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      console.log('Searching for:', query);
      const results = await searchStocks(query);
      console.log('Search results received:', results);
      console.log('Results count:', results?.length);
      setSearchResults(results);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Auto-search with debouncing
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Don't search if query is empty or if a symbol is already selected
    if (!searchQuery.trim() || selectedSymbol) {
      setSearchResults([]);
      return;
    }

    // Set new timeout for search (500ms delay)
    searchTimeoutRef.current = setTimeout(() => {
      handleSearch(searchQuery);
    }, 500);

    // Cleanup
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery, selectedSymbol]);

  const handleSelectStock = async (result: StockSearchResult) => {
    const symbol = result['1. symbol'];
    setSelectedSymbol(symbol);
    setSearchResults([]);
    setSearchQuery(symbol);
    
    // Fetch current price
    try {
      const response = await fetch(`/api/stocks/price/${symbol}`);
      const data = await response.json() as { price: number };
      setCurrentPrice(data.price);
    } catch (error) {
      console.error('Error fetching price:', error);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    console.log('Form submitted', { manualEntry, searchQuery, shares, totalAmount, purchasePrice });
    
    let finalShares: number;
    let finalPurchasePrice: number | undefined;
    
    if (manualEntry) {
      // Manual entry: use shares directly or calculate from total amount
      if (totalAmount && purchasePrice) {
        // Calculate shares from total amount and unit price
        finalShares = parseFloat(totalAmount) / parseFloat(purchasePrice);
        finalPurchasePrice = parseFloat(purchasePrice);
        console.log('Calculated from total:', { finalShares, finalPurchasePrice });
      } else if (shares) {
        // Use shares directly
        finalShares = parseFloat(shares);
        finalPurchasePrice = purchasePrice ? parseFloat(purchasePrice) : undefined;
        console.log('Using shares directly:', { finalShares, finalPurchasePrice });
      } else {
        console.error('Manual entry validation failed: need either shares or (totalAmount + purchasePrice)');
        alert('Please enter either:\n- Number of units, OR\n- Both Total Value and Unit Price');
        return;
      }
    } else {
      // Regular entry with API price
      if (inputMode === 'shares') {
        finalShares = parseFloat(shares);
      } else {
        // Calculate shares from total amount
        if (!currentPrice || !totalAmount) {
          console.error('Regular entry validation failed:', { currentPrice, totalAmount });
          return;
        }
        finalShares = parseFloat(totalAmount) / currentPrice;
      }
      finalPurchasePrice = purchasePrice ? parseFloat(purchasePrice) : undefined;
    }
    
    // For manual entry, use searchQuery as symbol if no symbol selected
    const symbolToUse = selectedSymbol || searchQuery.trim().toUpperCase();
    
    console.log('Validation check:', { symbolToUse, finalShares, isValid: !isNaN(finalShares) && finalShares > 0 });
    
    if (!symbolToUse) {
      alert('Please enter a symbol/name for your holding');
      return;
    }
    
    if (!finalShares || isNaN(finalShares) || finalShares <= 0) {
      alert('Invalid number of shares. Please check your inputs.');
      return;
    }

    // Add currency to notes for manual entries
    let finalNotes = notes || '';
    if (manualEntry && manualCurrency !== 'USD') {
      finalNotes = finalNotes ? `${finalNotes} [Currency: ${manualCurrency}]` : `Currency: ${manualCurrency}`;
    }

    console.log('Submitting:', { symbolToUse, finalShares, finalPurchasePrice, finalNotes });

    addMutation.mutate({
      symbol: symbolToUse,
      shares: finalShares,
      purchasePrice: finalPurchasePrice,
      purchaseDate: purchaseDate ? new Date(purchaseDate).getTime() : undefined,
      notes: finalNotes || undefined,
    });
  };
  
  // Update calculated values when inputs change
  const handleSharesChange = (value: string) => {
    setShares(value);
    if (currentPrice && value) {
      setTotalAmount((parseFloat(value) * currentPrice).toFixed(2));
    }
  };
  
  const handleAmountChange = (value: string) => {
    setTotalAmount(value);
    if (manualEntry && purchasePrice && value) {
      // For manual entry, calculate shares from total amount and purchase price
      setShares((parseFloat(value) / parseFloat(purchasePrice)).toFixed(4));
    } else if (currentPrice && value) {
      setShares((parseFloat(value) / currentPrice).toFixed(4));
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="card max-w-lg w-full">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Add Stock/ETF</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Search */}
          <div>
            <label className="label">Search Stock/ETF</label>
            <div className="relative">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setSelectedSymbol(''); // Clear selection when typing
                }}
                placeholder="Enter ticker (e.g., AAPL, VUAG.L) or company name..."
                className="input pr-10"
              />
              <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400">
                {isSearching ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-600"></div>
                ) : (
                  <Search className="w-5 h-5" />
                )}
              </div>
            </div>
            <div className="flex items-center justify-between mt-2">
              <p className="text-xs text-gray-500">
                💡 Tip: Search by ticker (AAPL, VUAG.L, SAP.DE) or company name.
              </p>
              <button
                type="button"
                onClick={() => {
                  setManualEntry(!manualEntry);
                  setSearchResults([]);
                  if (!manualEntry) {
                    setSelectedSymbol(searchQuery.trim().toUpperCase());
                  } else {
                    setSelectedSymbol('');
                  }
                }}
                className="text-xs text-primary-600 hover:text-primary-700 font-medium"
              >
                {manualEntry ? '← Back to Search' : 'Can\'t find it? Manual Entry →'}
              </button>
            </div>
            
            {manualEntry && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-xs text-blue-800 mb-2">
                  <strong>Manual Entry Mode:</strong> Enter a custom symbol/name for pension funds, private investments, or unlisted securities.
                </p>
                <p className="text-xs text-blue-700">
                  Example: "7BGI-BLACKROCK" or "UK-PENSION-FUND"
                </p>
              </div>
            )}

            {/* Search Results */}
            {!manualEntry && searchResults.length > 0 && (
              <div className="mt-2 border border-gray-200 rounded-lg max-h-60 overflow-y-auto">
                {searchResults.map((result, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => handleSelectStock(result)}
                    className="w-full px-4 py-3 text-left hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-900">{result['1. symbol']}</div>
                    <div className="text-sm text-gray-600">{result['2. name']}</div>
                    <div className="text-xs text-gray-500 mt-1">
                      {result['3. type']} • {result['4. region']}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Manual Entry Confirmation & Currency Selector */}
          {manualEntry && searchQuery.trim() && (
            <div className="space-y-3">
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-yellow-800">
                      <strong>Symbol:</strong> {searchQuery.trim().toUpperCase()}
                    </p>
                    <p className="text-xs text-yellow-700 mt-1">
                      Custom holding - prices won't auto-update
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-yellow-800 font-medium">Currency:</label>
                    <select
                      value={manualCurrency}
                      onChange={(e) => setManualCurrency(e.target.value)}
                      className="px-2 py-1 text-sm border border-yellow-300 rounded bg-white focus:outline-none focus:ring-2 focus:ring-yellow-500"
                    >
                      <option value="GBP">GBP (£)</option>
                      <option value="USD">USD ($)</option>
                      <option value="EUR">EUR (€)</option>
                      <option value="JPY">JPY (¥)</option>
                      <option value="CAD">CAD (C$)</option>
                      <option value="AUD">AUD (A$)</option>
                      <option value="CHF">CHF</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Input Mode Toggle */}
          {selectedSymbol && currentPrice && !manualEntry && (
            <div className="flex gap-2 p-1 bg-gray-100 rounded-lg">
              <button
                type="button"
                onClick={() => setInputMode('shares')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  inputMode === 'shares'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Enter Shares
              </button>
              <button
                type="button"
                onClick={() => setInputMode('amount')}
                className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  inputMode === 'amount'
                    ? 'bg-white text-primary-600 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                Enter Amount
              </button>
            </div>
          )}
          
          {/* Current Price Display */}
          {selectedSymbol && currentPrice && (
            <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
              <p className="text-sm text-blue-800">
                <span className="font-semibold">{selectedSymbol}</span> current price: <span className="font-bold">${currentPrice.toFixed(2)}</span>
              </p>
            </div>
          )}

          {/* Manual Entry: Total Value & Unit Price */}
          {manualEntry && (
            <div className="space-y-4 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label text-sm">Total Value</label>
                  <input
                    type="number"
                    value={totalAmount}
                    onChange={(e) => handleAmountChange(e.target.value)}
                    placeholder="10000.00"
                    step="0.01"
                    min="0"
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">From your statement</p>
                </div>
                <div>
                  <label className="label text-sm">Unit Price</label>
                  <input
                    type="number"
                    value={purchasePrice}
                    onChange={(e) => {
                      setPurchasePrice(e.target.value);
                      // Recalculate shares if total amount exists
                      if (totalAmount && e.target.value) {
                        setShares((parseFloat(totalAmount) / parseFloat(e.target.value)).toFixed(4));
                      }
                    }}
                    placeholder="45.20"
                    step="0.01"
                    min="0"
                    className="input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Price per unit</p>
                </div>
              </div>
              <div>
                <label className="label text-sm">Number of Units (calculated)</label>
                <input
                  type="number"
                  value={shares}
                  onChange={(e) => setShares(e.target.value)}
                  placeholder="221.2389"
                  step="0.0001"
                  min="0"
                  className="input bg-blue-50"
                  required
                />
                {totalAmount && purchasePrice && shares && (
                  <p className="text-xs text-green-600 mt-1">
                    ✓ {parseFloat(shares).toFixed(4)} units × {manualCurrency} {parseFloat(purchasePrice).toFixed(2)} = {manualCurrency} {parseFloat(totalAmount).toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          )}
          
          {/* Regular Entry: Shares Input */}
          {!manualEntry && (
            <div>
              <label className="label">Number of Shares</label>
              <input
                type="number"
                value={shares}
                onChange={(e) => handleSharesChange(e.target.value)}
                placeholder="0.0000"
                step="0.0001"
                min="0"
                className="input"
                required={inputMode === 'shares'}
                disabled={inputMode === 'amount' && !currentPrice}
              />
            </div>
          )}
          
          {/* Regular Entry: Total Amount Input */}
          {!manualEntry && (
            <div>
              <label className="label">Total Amount ($)</label>
              <input
                type="number"
                value={totalAmount}
                onChange={(e) => handleAmountChange(e.target.value)}
                placeholder="0.00"
                step="0.01"
                min="0"
                className="input"
                required={inputMode === 'amount'}
                disabled={inputMode === 'shares' && !currentPrice}
              />
              {inputMode === 'amount' && shares && (
                <p className="text-xs text-gray-500 mt-1">
                  This will purchase approximately <span className="font-medium">{parseFloat(shares).toFixed(4)}</span> shares
                </p>
              )}
            </div>
          )}

          {/* Optional: Purchase Price (for tracking cost basis) - Only for regular entries */}
          {!manualEntry && (
            <div className="border-t border-gray-200 pt-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-gray-700">📊 Optional: Track Performance</p>
                <span className="text-xs bg-blue-100 text-blue-700 px-2 py-1 rounded">
                  💡 Multiple lots supported
                </span>
              </div>
              <p className="text-xs text-gray-600 mb-3">
                You can add the same stock multiple times with different purchase prices/dates to track individual lots.
              </p>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label text-xs">Purchase Price</label>
                  <input
                    type="number"
                    value={purchasePrice}
                    onChange={(e) => setPurchasePrice(e.target.value)}
                    placeholder="0.00"
                    step="0.01"
                    min="0"
                    className="input text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">For gain/loss tracking</p>
                </div>
                
                <div>
                  <label className="label text-xs">Purchase Date</label>
                  <input
                    type="date"
                    value={purchaseDate}
                    onChange={(e) => setPurchaseDate(e.target.value)}
                    className="input text-sm"
                  />
                  <p className="text-xs text-gray-500 mt-1">When you bought it</p>
                </div>
              </div>
              
              <div className="mt-3">
                <label className="label text-xs">Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., Long-term hold, dividend play..."
                  className="input text-sm"
                  rows={2}
                />
              </div>
            </div>
          )}
          
          {/* Manual Entry: Notes and Purchase Date */}
          {manualEntry && (
            <div className="space-y-3">
              <div>
                <label className="label text-xs">Purchase Date (Optional)</label>
                <input
                  type="date"
                  value={purchaseDate}
                  onChange={(e) => setPurchaseDate(e.target.value)}
                  className="input text-sm"
                />
              </div>
              <div>
                <label className="label text-xs">Notes (Optional)</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="e.g., 7BGI BLACKROCK 50:50 GLO EQ ll - UK Workplace Pension"
                  className="input text-sm"
                  rows={2}
                />
                <p className="text-xs text-gray-500 mt-1">Add full fund name and details here</p>
              </div>
            </div>
          )}

          {/* Error */}
          {addMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-600">
                Failed to add holding. Please try again.
              </p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary flex-1"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary flex-1"
              disabled={
                addMutation.isPending ||
                (!manualEntry && !selectedSymbol) ||
                (manualEntry && !searchQuery.trim()) ||
                (!shares && !totalAmount)
              }
            >
              {addMutation.isPending ? 'Adding...' : 'Add Holding'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
